import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DESTROY,
  SetPRC,
  UPDATE,
  RESET,
} from "../../../.././../services/redux/slices/assets/persons/heads";
import { useToasts } from "react-toast-notifications";
import {
  signatoryName,
  Cloudinary,
  PresetUser,
} from "../../../../../services/utilities";
import Swal from "sweetalert2";
import { MDBIcon } from "mdbreact";
import "./style.css";
import { ImageDragAndDrop } from "../../../../../components/images";
import EditableField from "../../../../../components/customizable/editableField";
import {
  UPDATE_INFO,
  UPLOAD,
} from "../../../../../services/redux/slices/assets/persons/auth";
import EditableSelect from "../../../../../components/customizable/editableSelect";
import { Templates } from "../../../../../services/fakeDb";
import Cropper from "react-easy-crop";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { removeBackground } from "../../../../../components/images/backgroundRemover";

// these sections are signed by one person only, no validator
export const SINGLE_SIGNER_SECTIONS = ["Pathologist", "Radiologist"];

export default function Body() {
  const [showCropper, setShowCropper] = useState(false);
  const [cropperUser, setCropperUser] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentUpload, setCurrentUpload] = useState(null); // { email, _id }
  const {
      token,
      formSubmitted: fsAuth,
      isSuccess: isAuth,
    } = useSelector(({ auth }) => auth),
    { filtered, message, isSuccess, formSubmitted } = useSelector(
      ({ heads }) => heads
    ),
    { collections: personnels, isLoading: personnelsLoading } = useSelector(
      ({ personnels }) => personnels
    ),
    { addToast } = useToasts(),
    [currentPage, setCurrentPage] = useState(1),
    itemsPerPage = 6,
    [animateClass, setAnimateClass] = useState("");
  const dispatch = useDispatch();
  const history = useHistory();
  const hasEmployees = personnels?.length > 0;
  const [imageErrors, setImageErrors] = useState({});
  const [heads, setHeads] = useState([]);

  useEffect(() => {
    if (message) {
      addToast(message, {
        appearance: isSuccess ? "success" : "error",
      });
    }
  }, [isSuccess, message, addToast]);

  useEffect(() => {
    setHeads(filtered);
  }, [filtered]);

  const handleDelete = (_id, user) => {
    Swal.fire({
      title: `Are you sure you want to delete ${signatoryName(
        user.fullName
      )}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DESTROY({ token, data: { id: _id } })).then(() => {
          dispatch(RESET());
        });
      }
    });
  };

  const handlePageChange = (direction) => {
    setAnimateClass("fade-out");
    setTimeout(() => {
      setCurrentPage((prev) => (direction === "next" ? prev + 1 : prev - 1));
      setAnimateClass("fade-in");
    }, 300); // Delay matches animation duration
  };

  const handleUpdate = (data) => {
    dispatch(
      UPDATE({
        data,
        token,
      })
    );
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange("next");
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      handlePageChange("prev");
    }
  };

  const handleImageError = (email) => {
    if (imageErrors.hasOwnProperty(email))
      return setImageErrors((prev) => ({ ...prev }));
    setImageErrors((prev) => ({ ...prev, [email]: true }));
  };

  const updateAuth = (data) => {
    const { user, prc } = data;
    dispatch(UPDATE_INFO({ token, data: { _id: user, prc } })).then(
      ({ payload }) => {
        const { payload: info } = payload;
        const { _id, prc } = info;
        dispatch(SetPRC({ userId: _id, prc }));
      }
    );
  };

  const handleSections = (_department = "") => {
    const department = _department?.toLowerCase();
    const sections = Templates.getComponents(
      department === "laboratory"
        ? "LAB"
        : department === "radiology"
        ? "RAD"
        : "CLINIC"
    );
    sections.push(department === "laboratory" ? "Pathologist" : "Radiologist");
    return sections;
  };

  const updateHeadsImg = (_id, imgId, keyToUpdate, message = "") => {
    // the person may be a performer or a validator on any card
    const _heads = heads.map((head) => ({
      ...head,
      ...(head.user?._id === _id && {
        user: { ...head.user, [keyToUpdate]: imgId },
      }),
      ...(head.validator?._id === _id && {
        validator: { ...head.validator, [keyToUpdate]: imgId },
      }),
    }));

    setHeads(_heads);
    addToast(message, { appearance: "success" });
  };

  const handleSignature = async (e, email, _id) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    if (file.type === "image/png") {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          // dito na aalisin background bago ipasa kay Cropper
          const transparentImage = await removeBackground(reader.result, 60);
          setSelectedImage(transparentImage);
          setShowCropper(true);
          setCurrentUpload({ email, _id });
        } catch (err) {
          console.error("Background removal failed:", err);
          addToast("Failed to remove background", { appearance: "error" });
        }
      };
      reader.readAsDataURL(file);
    } else {
      addToast("Only PNG files are allowed!", { appearance: "error" });
    }
  };

  const handleUploadProfile = (base64, email, _id) => {
    const formData = Cloudinary.buildFileForm(
      base64,
      `users/${email}`,
      "profile"
    );
    dispatch(
      UPLOAD({
        data: formData,
        token,
      })
    ).then((action) =>
      dispatch(UPDATE_INFO({ data: { _id, pid: action.payload.imgId } })).then(
        () =>
          updateHeadsImg(
            _id,
            action.payload.imgId,
            "pid",
            "Profile Successfully uploaded!"
          )
      )
    );
  };

  const totalPages = Math.ceil(heads.length / itemsPerPage);
  const paginatedHeads = heads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCloseCropper = () => {
    setShowCropper(false);
    setSelectedImage(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCurrentUpload(null);
  };

  const getCroppedImg = (imageSrc, crop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.drawImage(
          image,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height
        );

        resolve(canvas.toDataURL("image/png"));
      };
      image.onerror = (err) => reject(err);
    });
  };

  const handleCropConfirm = async () => {
    try {
      const croppedImage = await getCroppedImg(
        selectedImage,
        croppedAreaPixels
      );

      const { email, _id } = currentUpload;
      const formData = Cloudinary.buildFileForm(
        croppedImage,
        `users/${email}`,
        "signature"
      );

      dispatch(UPLOAD({ data: formData, token })).then((action) => {
        dispatch(
          UPDATE_INFO({ data: { _id, sid: action.payload.imgId } })
        ).then(() =>
          updateHeadsImg(_id, action.payload.imgId, "sid", "Signature updated!")
        );
        setImageErrors((prev) => ({ ...prev, [email]: false }));
      });

      handleCloseCropper();
    } catch (err) {
      console.error(err);
      addToast("Failed to crop image!", { appearance: "error" });
    }
  };

  console.log("crper", cropperUser);

  // one signer panel (photo, signature, name, PRC) for the performer or validator
  const renderSigner = ({ head, person, role, index, label }) => {
    const isValidator = role === "validator",
      { _id } = head,
      {
        email = "",
        prc,
        pid = "",
        _id: personId = "",
        sid = "",
      } = person || {},
      inputId = `file-upload-${_id}-${role}`,
      // red only once the PRC expiration date has passed
      isExpired = !!prc?.to && new Date(prc.to) < new Date(new Date().toDateString()),
      openSignatureUpload = () => {
        setCropperUser(person);
        document.getElementById(inputId).click();
      },
      staff = personnels.map(({ user: p }) => ({
        userId: p?._id,
        text: signatoryName(p?.fullName),
      })),
      nameCollections = isValidator
        ? [
            { userId: "none", text: "None" },
            ...staff.filter(({ userId }) => userId !== head.user?._id),
          ]
        : staff;

    return (
      <div className={`signatories-signer ${person ? "" : "is-empty"}`}>
        <span className="signatories-card-role-label">
          {label || (isValidator ? "Validated by:" : "Performed by:")}
        </span>
        <div className="signatories-signer-photo">
          {person ? (
            <ImageDragAndDrop
              key={`${personId}-${pid}`}
              img={`${Cloudinary.getEndpoint()}/${pid}/users/${email}/profile.png`}
              handleUpload={(cropImg) =>
                handleUploadProfile(cropImg, email, personId)
              }
              formSubmitted={fsAuth}
              allowedType="jpg"
            />
          ) : (
            <img
              src={PresetUser}
              alt="No validator"
              className="signatories-signer-placeholder"
            />
          )}
        </div>

        {person && (
          <>
            <div
              className={`signatories-card-signature-container ${
                !imageErrors[email] ? "has-signature" : ""
              }`}
            >
              {!imageErrors[email] ? (
                <img
                  key={sid}
                  onClick={openSignatureUpload}
                  alt="Signature"
                  src={`${Cloudinary.getEndpoint()}/${sid}/users/${email}/signature.png`}
                  onError={() => handleImageError(email)}
                  className="signatories-card-signature"
                />
              ) : (
                <button
                  className="signatories-card-signature-upload-btn"
                  onClick={openSignatureUpload}
                >
                  Upload Signature
                </button>
              )}
            </div>
            <input
              id={inputId}
              type="file"
              accept="image/png"
              onChange={(e) => handleSignature(e, email, personId)}
              hidden
            />
          </>
        )}

        <div
          className="signatories-card-name-row position-relative d-flex justify-content-center"
          style={{ height: "1.6rem" }}
        >
          <EditableSelect
            title="Click to edit"
            classNameTxt="signatories-card-name"
            isEditable
            preValue={personId || "none"}
            animation
            className="mb-n2 mt-n1"
            animationStyle={{ width: "100%" }}
            collections={nameCollections}
            fieldData={{
              _id,
              userId: personId || "none",
              text: person ? signatoryName(person.fullName) : "None",
            }}
            keyForValue="userId"
            keyForText="text"
            formSubmitted={formSubmitted}
            isSuccess={isSuccess}
            onSave={(data) =>
              handleUpdate(
                isValidator
                  ? {
                      id: data._id,
                      validator: data.userId === "none" ? null : data.userId,
                    }
                  : { id: data._id, user: data.userId }
              )
            }
          />
        </div>

        {person && (
          <div className="signatories-card-expiration-container">
            <span className="signatories-card-prc-label">
              <strong>PRC:&nbsp;</strong>
              <EditableField
                title="Click to edit"
                className="form-control form-control-sm"
                classNameTxt="signatories-card-prc"
                type="string"
                animation
                animationStyle={{ width: "10rem" }}
                keyForValue="id"
                fieldData={{
                  _id: `${_id}-${role}-id-${index}`,
                  id: prc?.id,
                  prc,
                  user: personId,
                }}
                onSave={(data) =>
                  updateAuth({
                    ...data,
                    prc: { ...data.prc, id: data.id },
                  })
                }
                formSubmitted={fsAuth}
                isSuccess={isAuth}
              />
            </span>
            <span>&nbsp;|&nbsp;</span>
            <span
              className={`signatories-card-expiration-label ${
                isExpired ? "is-expired" : ""
              }`}
              title={isExpired ? "PRC license is expired" : ""}
            >
              <strong>Exp:&nbsp;</strong>
              <EditableField
                title="Click to edit"
                className="form-control form-control-sm"
                classNameTxt="signatories-card-expiration"
                type="date"
                width="11rem"
                animation
                animationStyle={{ width: "10rem" }}
                keyForValue="to"
                fieldData={{
                  _id: `${_id}-${role}-to`,
                  to: prc?.to,
                  prc,
                  user: personId,
                }}
                onSave={(data) =>
                  updateAuth({
                    ...data,
                    prc: { ...data.prc, to: data.to },
                  })
                }
                formSubmitted={fsAuth}
                isSuccess={isAuth}
              />
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="signatories-section position-relative">
      {showCropper &&
        createPortal(
          <div className="signatories-cropper-modal">
            <div className="signatories-cropper-container">
              <div className="signatories-cropper-header">
                ✂️ Crop Signature
              </div>
              <div className="signatories-cropper-body">
                <Cropper
                  image={selectedImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={2 / 1}
                  cropSize={{ width: 200, height: 100 }}
                  minZoom={0.1}
                  maxZoom={5}
                  zoomSpeed={0.2}
                  showGrid={false}
                  restrictPosition={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, croppedAreaPixels) =>
                    setCroppedAreaPixels(croppedAreaPixels)
                  }
                />
                <span className="signatories-cropper-guide">
                  {signatoryName(cropperUser?.fullName)}
                </span>
              </div>
              <div className="signatories-cropper-controls">
                <input
                  type="range"
                  className="signatories-crop-zoom-slider"
                  min={0.1}
                  max={5}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
                <button
                  className="signatories-cropper-btn cancel"
                  onClick={handleCloseCropper}
                >
                  Cancel
                </button>
                <button
                  className="signatories-cropper-btn confirm"
                  onClick={handleCropConfirm}
                >
                  Crop & Upload
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      <div
        className={`${
          paginatedHeads.length > 0 && "signatories-card-container"
        } mt-4 ${animateClass}`}
      >
        {paginatedHeads.length > 0 ? (
          paginatedHeads.map((head, index) => {
            const { _id, department, section, user, validator } = head,
              isSingleSigner = SINGLE_SIGNER_SECTIONS.includes(section),
              missingPRC =
                !user?.prc ||
                (!isSingleSigner && validator && !validator.prc);

            return (
              <div
                key={_id || index}
                className={`signatories-card ${missingPRC ? "requiredPRC" : ""}`}
              >
                <div className="signatories-card-top">
                  <div className="signatories-card-section-department">
                    <div>
                      <EditableSelect
                        title="Click to edit"
                        classNameTxt="signatories-card-section"
                        animation
                        className="mb-n2 mt-n1"
                        animationStyle={{ width: "14rem" }}
                        isEditable
                        preValue={section}
                        collections={handleSections(department)}
                        fieldData={{
                          _id,
                          section,
                        }}
                        keyForValue="section"
                        selectStyle={{ width: "11rem" }}
                        keyForText="section"
                        formSubmitted={formSubmitted}
                        isSuccess={isSuccess}
                        onSave={(data) =>
                          handleUpdate({
                            id: data._id,
                            section: data.section,
                            ...(SINGLE_SIGNER_SECTIONS.includes(
                              data.section
                            ) && { validator: null }),
                          })
                        }
                      />
                    </div>
                    &nbsp;-&nbsp;
                    <span>
                      <EditableSelect
                        title="Click to edit"
                        classNameTxt="signatories-card-department"
                        isEditable
                        animation
                        className="mb-n2 mt-n1"
                        animationStyle={{ width: "14rem" }}
                        preValue={department}
                        collections={Templates.collections}
                        fieldData={{
                          _id,
                          label: department,
                        }}
                        keyForValue="label"
                        keyForText="label"
                        formSubmitted={formSubmitted}
                        isSuccess={isSuccess}
                        onSave={(data) =>
                          handleUpdate({
                            id: data._id,
                            department: data.label,
                          })
                        }
                      />
                    </span>
                  </div>
                </div>

                <div
                  className={`signatories-signers ${
                    isSingleSigner ? "is-single" : ""
                  }`}
                >
                  {renderSigner({
                    head,
                    person: user,
                    role: "performer",
                    index,
                    label: isSingleSigner && `${section}:`,
                  })}
                  {!isSingleSigner &&
                    renderSigner({
                      head,
                      person: validator,
                      role: "validator",
                      index,
                    })}
                </div>

                {missingPRC && (
                  <span className="signatories-card-footer-requiredPRC">
                    <strong>PRC license is required</strong> for every
                    signatory of this section. This is a&nbsp;
                    <strong>DOH qualification</strong> for publishing
                    laboratory results.
                  </span>
                )}
                <div className="signatories-card-actionBtn">
                  <button
                    className="signatories-card-btn-delete bg-danger"
                    onClick={() => {
                      handleDelete(_id, user);
                    }}
                  >
                    <MDBIcon fas icon="times" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div
            className="signatories-card empty-card d-flex flex-column align-items-center  justify-content-center p-4"
            style={{
              border: "2px dashed #ccc",
              borderRadius: "0.5rem",
              backgroundColor: "#f9f9f9",
              width: "14rem",
              height: "18rem",
              textAlign: "center",
            }}
          >
            {personnelsLoading ? (
              <>
                <MDBIcon
                  icon="spinner"
                  pulse
                  size="3x"
                  className="mb-3 text-muted"
                />
                <h6 className="text-muted mb-2">Checking employees...</h6>
              </>
            ) : hasEmployees ? (
              <>
                <MDBIcon
                  icon="user-slash"
                  size="3x"
                  className="mb-3 text-muted"
                />
                <h6 className="text-muted mb-2">No Signatories Yet</h6>
                <p className="text-center text-muted small">
                  Add signatories to display them here.
                </p>
              </>
            ) : (
              <>
                <MDBIcon
                  icon="user-plus"
                  size="3x"
                  className="mb-3 text-muted"
                />
                <h6 className="text-muted mb-2">No Employees Yet</h6>
                <p className="text-center text-muted small">
                  Signatories are chosen from your employees. Add employees in
                  File 201 first.
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm btn-rounded"
                  onClick={() => history.push("/manager/hr/file201")}
                >
                  <MDBIcon icon="folder" className="mr-2" />
                  Go to File 201
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {paginatedHeads.length === 0 ? (
        ""
      ) : (
        <>
          <button
            className="signatories-pagination-btnLeft"
            onClick={prevPage}
            disabled={currentPage === 1}
          >
            <MDBIcon icon="angle-left" />
          </button>
          <button
            className="signatories-pagination-btnRight"
            onClick={nextPage}
            disabled={currentPage === totalPages}
          >
            <MDBIcon icon="angle-right" />
          </button>
        </>
      )}
    </div>
  );
}
